import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type { AnswerResponse, AnswerValue, GameState, Me, PublicPuzzle } from '@/lib/api'
import { ErrorNotice } from '@/components/ErrorNotice'
import { PuzzleRenderer } from '@/game/PuzzleRenderer'
import { MediaGallery } from '@/game/MediaGallery'
import { FinishedScreen } from '@/game/FinishedScreen'
import { resizeImageForUpload } from '@/lib/imageResize'
import { HintPanel } from '@/game/HintPanel'

type Props = { me: Me; onLoggedOut: () => void }

export type Feedback =
  | { kind: 'correct'; pointsAwarded: number }
  | { kind: 'wrong'; penaltySeconds: number; attemptsRemaining: number | null }
  | { kind: 'failed'; penaltySeconds: number; revealText: string | null; outOfAttempts: boolean }
  | { kind: 'info'; message: string }
  | { kind: 'error'; message: string }

/**
 * Game shell. The server is the game master: this component only renders the
 * state it is given and re-fetches GET /api/game whenever anything is unclear
 * (stale puzzle, conflicts, network hiccups, page refresh).
 */
export function GameScreen({ me, onLoggedOut }: Props) {
  const [game, setGame] = useState<GameState | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const inFlight = useRef(false)

  const resync = useCallback(async () => {
    try {
      const { game } = await api.game()
      setGame(game)
      setLoadError(null)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onLoggedOut()
      else setLoadError(err instanceof Error ? err.message : String(err))
    }
  }, [onLoggedOut])

  useEffect(() => {
    void resync()
  }, [resync])

  // "Correct!" stays briefly on the next puzzle, then clears. A "wrong, here is
  // the answer" banner stays until the player interacts with the next puzzle.
  const puzzleId = game?.puzzle?.id
  useEffect(() => {
    if (!puzzleId) return
    const id = setTimeout(() => setFeedback((f) => (f?.kind === 'correct' ? null : f)), 2500)
    return () => clearTimeout(id)
  }, [puzzleId])

  const submit = useCallback(
    async (value: AnswerValue) => {
      if (!game?.puzzle || inFlight.current) return
      inFlight.current = true
      setSubmitting(true)
      setFeedback(null)
      try {
        const res: AnswerResponse = await api.answer(game.puzzle.id, value)
        const previous = game.puzzle
        setGame(res.game)
        if (res.result === 'correct') setFeedback({ kind: 'correct', pointsAwarded: res.pointsAwarded })
        else if (res.result === 'failed')
          setFeedback({
            kind: 'failed',
            penaltySeconds: res.penaltySecondsAdded,
            revealText: describeReveal(previous, res.reveal),
            outOfAttempts: previous.onWrongAnswer !== 'advance',
          })
        else setFeedback({ kind: 'wrong', penaltySeconds: res.penaltySecondsAdded, attemptsRemaining: res.attemptsRemaining })
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 401) return onLoggedOut()
          if (err.status === 409 || err.status === 404) {
            // Stale/future puzzle, finished run: the server knows best. Resync.
            setFeedback({ kind: 'info', message: 'Your game state was refreshed.' })
            await resync()
          } else {
            setFeedback({ kind: 'error', message: err.message })
          }
        } else {
          setFeedback({ kind: 'error', message: 'Network error. Your answer may not have been sent.' })
          await resync()
        }
      } finally {
        inFlight.current = false
        setSubmitting(false)
      }
    },
    [game, onLoggedOut, resync],
  )

  const act = useCallback(
    async (action: string) => {
      if (!game?.puzzle) return
      try {
        const { game: next } = await api.act(game.puzzle.id, action)
        setGame(next)
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return onLoggedOut()
        setFeedback({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
        await resync()
      }
    },
    [game, onLoggedOut, resync],
  )

  const uploadPhoto = useCallback(
    async (file: File, onProgress?: (fraction: number) => void) => {
      if (!game?.puzzle || inFlight.current) return
      inFlight.current = true
      setSubmitting(true)
      setFeedback(null)
      try {
        const prepared = await resizeImageForUpload(file)
        const res = await api.uploadPhoto(game.puzzle.id, prepared, onProgress)
        setGame(res.game)
        setFeedback(
          res.result === 'correct'
            ? { kind: 'correct', pointsAwarded: res.pointsAwarded }
            : { kind: 'error', message: 'The photo was not accepted.' },
        )
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return onLoggedOut()
        setFeedback({ kind: 'error', message: err instanceof Error ? err.message : 'Upload failed. Please try again.' })
        if (err instanceof ApiError && (err.status === 409 || err.status === 404)) await resync()
      } finally {
        inFlight.current = false
        setSubmitting(false)
      }
    },
    [game, onLoggedOut, resync],
  )

  const useHint = useCallback(
    async (hintId: string) => {
      if (!game?.puzzle) return
      try {
        const { game: next } = await api.useHint(game.puzzle.id, hintId)
        setGame(next)
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return onLoggedOut()
        setFeedback({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
        await resync()
      }
    },
    [game, onLoggedOut, resync],
  )

  async function logout() {
    try {
      await api.logout()
    } finally {
      onLoggedOut()
    }
  }

  if (loadError) {
    return (
      <main>
        <ErrorNotice message={loadError} onRetry={() => void resync()} />
      </main>
    )
  }
  if (!game) {
    return (
      <main>
        <p className="muted" role="status">
          Loading your game…
        </p>
      </main>
    )
  }

  if (game.run.status === 'finished' || !game.puzzle) {
    return <FinishedScreen game={game} onLogout={() => void logout()} />
  }

  const { puzzle, progress } = game

  return (
    <main className="game">
      <header className="game-header">
        <span className="muted">{game.event.name}</span>
        <span className="round">
          Round {game.puzzleNumber} / {game.totalPuzzles}
        </span>
        <span className="score" aria-label="Score">
          {game.run.score} <span className="muted">/ {game.maxScore} pts</span>
        </span>
      </header>

      <article className="card" key={puzzle.id}>
        <h1>{puzzle.title}</h1>
        <p className="prompt">{puzzle.prompt}</p>
        <MediaGallery media={puzzle.media} />

        <PuzzleRenderer
          puzzle={puzzle}
          progress={progress}
          serverTime={game.serverTime}
          onSubmit={submit}
          onAction={act}
          onUploadPhoto={uploadPhoto}
          onResync={resync}
          disabled={submitting}
        />

        <FeedbackBanner feedback={feedback} />

        <HintPanel hints={puzzle.hints} onReveal={useHint} disabled={submitting} />

        <footer className="puzzle-footer muted">
          <span>Worth {puzzle.points} points</span>
          {progress && progress.attemptsRemaining !== null && (
            <span>
              {progress.attemptsRemaining} attempt{progress.attemptsRemaining === 1 ? '' : 's'} left
            </span>
          )}
          {puzzle.onWrongAnswer === 'advance' && <span>One shot: a wrong answer moves you on</span>}
        </footer>
      </article>

      <p className="game-actions">
        <button type="button" onClick={() => void resync()} disabled={submitting}>
          Refresh
        </button>
        <button type="button" onClick={() => void logout()} disabled={submitting}>
          Log out
        </button>
        <span className="muted">
          {me.player.email}
          {me.player.location ? ` · ${me.player.location.name}` : ''}
        </span>
      </p>
    </main>
  )
}

function FeedbackBanner({ feedback }: { feedback: Feedback | null }) {
  if (!feedback) return null
  switch (feedback.kind) {
    case 'correct':
      return (
        <p className="feedback status-ok" role="status">
          Correct!{feedback.pointsAwarded > 0 && ` +${feedback.pointsAwarded} points.`} On to the next one.
        </p>
      )
    case 'wrong':
      return (
        <p className="feedback status-err" role="alert">
          Not quite. Try again.
          {feedback.attemptsRemaining !== null &&
            ` ${feedback.attemptsRemaining} attempt${feedback.attemptsRemaining === 1 ? '' : 's'} left.`}
        </p>
      )
    case 'failed':
      return (
        <p className="feedback status-err" role="alert">
          {feedback.outOfAttempts ? 'Out of attempts for that puzzle.' : 'Not quite.'}
          {feedback.revealText && ` The correct answer was: ${feedback.revealText}.`}
          {' No points this time. Moving on.'}
        </p>
      )
    case 'info':
      return (
        <p className="feedback muted" role="status">
          {feedback.message}
        </p>
      )
    case 'error':
      return (
        <p className="feedback status-err" role="alert">
          {feedback.message}
        </p>
      )
  }
}

/** Turns a type-specific reveal into a short human-readable string using the puzzle's public labels. */
function describeReveal(puzzle: PublicPuzzle, reveal: Record<string, unknown> | null): string | null {
  if (!reveal) return null
  const cfg = puzzle.publicConfig as Record<string, unknown>
  const label = (list: unknown, id: unknown) => {
    const items = Array.isArray(list) ? (list as { id: string; label: string }[]) : []
    return items.find((i) => i.id === id)?.label ?? String(id)
  }
  switch (puzzle.type) {
    case 'choice':
      return (reveal.correctOptionIds as string[] | undefined)?.map((id) => label(cfg.options, id)).join(', ') ?? null
    case 'text':
      return typeof reveal.acceptedAnswer === 'string' ? reveal.acceptedAnswer : null
    case 'number': {
      if (reveal.mode === 'exact' || reveal.mode === 'tolerance') return String(reveal.value)
      if (reveal.mode === 'range') return `between ${String(reveal.min)} and ${String(reveal.max)}`
      return null
    }
    case 'ordering':
      return (reveal.orderedIds as string[] | undefined)?.map((id) => label(cfg.items, id)).join(' → ') ?? null
    case 'matching': {
      const pairs = reveal.pairs as Record<string, string> | undefined
      return pairs ? Object.entries(pairs).map(([l, r]) => `${label(cfg.left, l)} – ${label(cfg.right, r)}`).join(', ') : null
    }
    case 'categorization': {
      const a = reveal.assignments as Record<string, string> | undefined
      return a ? Object.entries(a).map(([i, c]) => `${label(cfg.items, i)}: ${label(cfg.categories, c)}`).join(', ') : null
    }
    default:
      return null
  }
}
